/**
 * Startup diagnostics and auto-healing
 * Runs before React mounts to detect and fix common issues
 */

import { logger } from '@/lib/logger';
import { getMissingFirebaseVars } from '@/lib/env/requireEnv';
interface DiagnosticResult {
  ok: boolean;
  message: string;
  severity: 'info' | 'warning' | 'error';
}

/**
 * Clear all service workers (dev only)
 */
async function clearServiceWorkers(): Promise<void> {
  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((reg) => reg.unregister()));
      if (registrations.length > 0) {
        logger.info(`[Diagnostics] Cleared ${registrations.length} service worker(s)`);
      }
    } catch (error) {
      logger.warn('[Diagnostics] Failed to clear service workers:', error);
    }
  }
}

// Track if we've already shown the cache warning this session
let hasShownCacheWarning = false;

/**
 * Check if Vite cache might be stale (DEV only, once per session)
 * Only warns if explicitly enabled via localStorage flag
 */
function checkViteCache(): DiagnosticResult | null {
  // Only check in development mode
  if (!import.meta.env.DEV) {
    return null;
  }

  // Only show once per session
  if (hasShownCacheWarning) {
    return null;
  }

  // Only show if user has explicitly enabled cache warnings
  // (Most developers don't need this noise)
  if (localStorage.getItem('debug_vite_cache') !== 'true') {
    return null;
  }

  const lastClear = localStorage.getItem('vite_cache_cleared_at');
  const now = Date.now();
  const oneDay = 24 * 60 * 60 * 1000;

  if (!lastClear || now - parseInt(lastClear) > oneDay) {
    hasShownCacheWarning = true;
    return {
      ok: false,
      message: 'Vite cache may be stale. Run: npm run clean:vite',
      severity: 'warning',
    };
  }

  return {
    ok: true,
    message: 'Vite cache is recent',
    severity: 'info',
  };
}

/**
 * Check critical environment variables
 * Note: Firebase config validation now happens in requireEnv.ts
 * This just reports status for diagnostics logging
 */
function checkEnvironment(): DiagnosticResult[] {
  const results: DiagnosticResult[] = [];

  // Check Firebase config status
  const missingVars = getMissingFirebaseVars();

  if (missingVars.length > 0) {
    // This shouldn't happen - requireEnv.ts should have thrown already
    // But include for completeness
    results.push({
      ok: false,
      message: `Missing Firebase env vars: ${missingVars.join(', ')}`,
      severity: 'error',
    });
  } else {
    results.push({
      ok: true,
      message: 'Firebase configuration loaded from environment',
      severity: 'info',
    });
  }

  return results;
}

/**
 * Log system information (only in verbose mode)
 */
function logSystemInfo(): void {
  // Only log if explicitly enabled via localStorage
  if (localStorage.getItem('debug_diagnostics') === 'true') {
    const info = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      online: navigator.onLine,
      cookiesEnabled: navigator.cookieEnabled,
      screenSize: `${window.screen.width}x${window.screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      mode: import.meta.env.MODE,
      dev: import.meta.env.DEV,
      prod: import.meta.env.PROD,
    };

    logger.info('[Diagnostics] System Info:', info);
  }
}

/**
 * Run all startup diagnostics
 */
export async function runStartupDiagnostics(): Promise<void> {
  // Only log if verbose debugging is enabled
  const verbose = localStorage.getItem('debug_diagnostics') === 'true';

  if (verbose) {
    logger.info('[Diagnostics] Running startup checks...');
  }

  // Log system info (only if verbose)
  logSystemInfo();

  // Clear service workers in dev mode
  if (import.meta.env.DEV) {
    await clearServiceWorkers();
  }

  // Check environment - only log warnings and errors
  const envResults = checkEnvironment();
  envResults.forEach((result) => {
    if (result.severity === 'error') {
      logger.error(`[Diagnostics] ${result.message}`);
    } else if (result.severity === 'warning') {
      logger.warn(`[Diagnostics] ${result.message}`);
    }
    // Skip info logs unless verbose
  });

  // Check Vite cache - only in DEV and only if enabled
  const cacheResult = checkViteCache();
  if (cacheResult?.severity === 'warning') {
    logger.warn(`[Diagnostics] ${cacheResult.message}`);
  }

  if (verbose) {
    logger.info('[Diagnostics] Startup checks complete');
  }
}

/**
 * Mark Vite cache as cleared (call after manual cache clear)
 */
export function markCacheCleared(): void {
  localStorage.setItem('vite_cache_cleared_at', Date.now().toString());
}
